/**
 * Provides the button-with-dropdown configuration for the PharmaStpFile table toolbar.
 *
 * Each entry maps a logical action key to the handler supplied by the consuming component,
 * keeping the static metadata (ids, labels, grouping) decoupled from runtime wiring.
 * Organization type controls which toolbar button groups render: NYCBS Stats shows only
 * File Upload and Run Delivery Pipeline via plain toolbar buttons; other orgs show SFTP Files
 * and Push to SFTP as split buttons with menus, plus Reassign PA and Run Delivery Pipeline
 * as plain buttons in display order.
 */

import {
  ButtonWithDropdownMeta,
  TableToolbarItem,
} from "../../../data-model/customTableModel";
import { OrganizationType } from "../../../enums/organizationTypes";

/** Unique identifier for each button group in the toolbar. */
export type ButtonGroupId = "sftp-summary" | "send-to-sftp";

/** Handler keys required to wire all toolbar dropdown groups. */
export type BaseHandlerKey =
  | "handleDownloadFiles"
  | "handleSummary"
  | "handleDownloadFilesModal"
  | "handleTrackStatus"
  | "handleTextNote"
  | "handleSendToSftp"
  | "handleSummaryStats";

export type DropdownHandlerKey = BaseHandlerKey;

export type DropdownHandlers = Record<BaseHandlerKey, () => void>;

/** Metadata for a plain toolbar Button rendered via CustomTable `otherButtonMeta`. */
export interface ToolbarButtonMeta {
  text: string;
  onClick: () => void;
  disabled: boolean;
  buttonType: "primary" | "secondary" | "tertiary";
  size: "small" | "medium" | "large";
}

interface ButtonGroupConfig extends ButtonWithDropdownMeta {
  groupId: ButtonGroupId;
}

interface DropdownConfigParams {
  handlers: DropdownHandlers;
  isLoading: boolean;
  organizationType: string;
}

const NYCBS_STATS_ORG_TYPES: ReadonlySet<string> = new Set([
  OrganizationType.NYCBS_STATS,
  OrganizationType.NYCBS_STATS_DEV,
]);

/**
 * Whether the organization uses the NYCBS Stats STP file toolbar variant (primary File Upload;
 * no SFTP Files / Push to SFTP dropdown groups from this module).
 *
 * @param organizationType - Current organization identifier from settings
 * @returns True when org is NYCBS Stats or NYCBS Stats Dev
 */
export const isNycbsStatsOrganization = (organizationType: string): boolean =>
  NYCBS_STATS_ORG_TYPES.has(organizationType);

/**
 * Returns the set of button group IDs visible for a given organization type.
 *
 * @param organizationType - The current organization's identifier
 * @returns Set of ButtonGroupIds that should be rendered
 */
const getVisibleGroups = (
  organizationType: string,
): ReadonlySet<ButtonGroupId> => {
  if (isNycbsStatsOrganization(organizationType)) {
    return new Set<ButtonGroupId>();
  }
  return new Set<ButtonGroupId>(["sftp-summary", "send-to-sftp"]);
};

/**
 * Builds the full set of button group definitions wired to the given handlers.
 *
 * @param handlers - Map of action callbacks
 * @param isLoading - Whether the table is currently loading data
 * @returns All possible button groups with their metadata
 */
const buildAllGroups = (
  handlers: DropdownHandlers,
  isLoading: boolean,
): ButtonGroupConfig[] => [
  {
    groupId: "sftp-summary",
    text: "SFTP Files",
    disabled: isLoading,
    buttonType: "secondary",
    size: "medium",
    menuData: [
      {
        id: "sftp_input_files",
        label: "SFTP Input Files",
        onClick: handlers.handleDownloadFiles,
      },
      {
        id: "summary-stats",
        label: "Summary Stats",
        onClick: handlers.handleSummary,
      },
      {
        id: "download-files",
        label: "Download Files",
        onClick: handlers.handleDownloadFilesModal,
      },
    ],
  },
  {
    groupId: "send-to-sftp",
    text: "Push to SFTP",
    disabled: isLoading,
    buttonType: "primary",
    size: "medium",
    menuData: [
      {
        id: "push-batch-file",
        label: "Push Batch File",
        onClick: handlers.handleSendToSftp,
      },
      {
        id: "push-pending-records",
        label: "Push Pending Records",
        onClick: handlers.handleSummaryStats,
      },
    ],
  },
];

/**
 * Builds the OncoEMR upload button for NYCBS pharmacy and Stats organizations.
 * Opens the stat file upload modal directly (no dropdown).
 *
 * @param onFileUpload - Callback invoked when the button is clicked
 * @param isLoading - Whether the table is currently loading data
 * @param organizationType - The current organization's identifier
 * @returns Button metadata array, or undefined when the organization does not support NYCBS uploads
 */
export const buildFileUploadButtonMeta = (
  onFileUpload: () => void,
  isLoading: boolean,
  organizationType: string,
): ToolbarButtonMeta[] | undefined => {
  if (
    !isNycbsStatsOrganization(organizationType) &&
    organizationType !== OrganizationType.NYCBS_PHARMA
  ) {
    return undefined;
  }

  return [
    {
      text: "Upload & run PA",
      disabled: isLoading,
      buttonType: "primary",
      size: "medium",
      onClick: onFileUpload,
    },
  ];
};

/**
 * Builds a plain Run Delivery Pipeline toolbar button that opens the delivery pipeline modal.
 *
 * @param onRunDeliveryPipeline - Callback invoked when the button is clicked
 * @param isLoading - Whether the table is currently loading data
 * @returns Button metadata for `CustomTable` `otherButtonMeta`
 */
export const buildRunDeliveryPipelineButtonMeta = (
  onRunDeliveryPipeline: () => void,
  isLoading: boolean,
): ToolbarButtonMeta => ({
  text: "Run Delivery Pipeline",
  onClick: onRunDeliveryPipeline,
  disabled: isLoading,
  buttonType: "secondary",
  size: "medium",
});

interface PharmaStpFileOtherButtonsParams {
  onReassignCase: () => void;
  onStatFileUpload: () => void;
  onRunDeliveryPipeline: () => void;
  isLoading: boolean;
  organizationType: string;
}

/**
 * Builds plain header buttons for the Pharma STP File table: Reassign PA, optional File Upload,
 * and Run Delivery Pipeline. Order is chosen so that, after CustomTable reverses `otherButtonMeta`,
 * Run Delivery Pipeline sits closest to the dropdown toolbar buttons and Refresh.
 *
 * Prefer `buildPharmaStpFileToolbarItems` for the STP file table, which preserves display order
 * without relying on `otherButtonMeta` reversal.
 *
 * @param params - Toolbar handlers, loading flag, and organization type
 * @returns Non-empty button metadata array for `CustomTable` `otherButtonMeta`
 */
export const buildPharmaStpFileOtherButtonMeta = ({
  onReassignCase,
  onStatFileUpload,
  onRunDeliveryPipeline,
  isLoading,
  organizationType,
}: PharmaStpFileOtherButtonsParams): ToolbarButtonMeta[] => {
  const reassignButton: ToolbarButtonMeta = {
    text: "Reassign PA",
    onClick: onReassignCase,
    disabled: isLoading,
    buttonType: "secondary",
    size: "medium",
  };

  const runDeliveryPipelineButton = buildRunDeliveryPipelineButtonMeta(
    onRunDeliveryPipeline,
    isLoading,
  );

  const fileUploadMeta = buildFileUploadButtonMeta(
    onStatFileUpload,
    isLoading,
    organizationType,
  );

  if (fileUploadMeta && fileUploadMeta.length > 0) {
    return [reassignButton, ...fileUploadMeta, runDeliveryPipelineButton];
  }

  return [reassignButton, runDeliveryPipelineButton];
};

/**
 * Builds the toolbar dropdown button groups for the PharmaStpFile table,
 * filtered by organization type.
 *
 * @param params - Handler map, loading flag, and current organization type
 * @returns Array of ButtonWithDropdownMeta visible for the organization
 */
export const buildButtonDropdownConfig = ({
  handlers,
  isLoading,
  organizationType,
}: DropdownConfigParams): ButtonWithDropdownMeta[] => {
  const visibleGroups = getVisibleGroups(organizationType);
  return buildAllGroups(handlers, isLoading)
    .filter((group) => visibleGroups.has(group.groupId))
    .map(({ groupId: _, ...meta }) => meta);
};

interface PharmaStpFileToolbarParams extends PharmaStpFileOtherButtonsParams {
  handlers: DropdownHandlers;
}

/**
 * Builds the Pharma STP File table toolbar in display order:
 * Reassign PA, SFTP Files, Run Delivery Pipeline, Push to SFTP (non-NYCBS orgs).
 *
 * @param params - Toolbar handlers, dropdown handlers, loading flag, and organization type
 * @returns Ordered toolbar items for `CustomTable` `orderedToolbarMeta`
 */
export const buildPharmaStpFileToolbarItems = ({
  onReassignCase,
  onStatFileUpload,
  onRunDeliveryPipeline,
  handlers,
  isLoading,
  organizationType,
}: PharmaStpFileToolbarParams): TableToolbarItem[] => {
  const reassignButton: TableToolbarItem = {
    kind: "button",
    text: "Reassign PA",
    onClick: onReassignCase,
    disabled: isLoading,
    buttonType: "secondary",
    size: "medium",
  };

  const runDeliveryPipelineButton: TableToolbarItem = {
    kind: "button",
    ...buildRunDeliveryPipelineButtonMeta(onRunDeliveryPipeline, isLoading),
  };

  const fileUploadMeta = buildFileUploadButtonMeta(
    onStatFileUpload,
    isLoading,
    organizationType,
  );

  if (isNycbsStatsOrganization(organizationType) && fileUploadMeta?.length) {
    return [
      reassignButton,
      ...fileUploadMeta.map((button): TableToolbarItem => ({
        kind: "button",
        ...button,
      })),
      runDeliveryPipelineButton,
    ];
  }

  const dropdownGroups = buildButtonDropdownConfig({
    handlers,
    isLoading,
    organizationType,
  });

  const [sftpFilesGroup, pushToSftpGroup] = dropdownGroups;

  return [
    reassignButton,
    ...(fileUploadMeta ?? []).map((button): TableToolbarItem => ({
      kind: "button",
      ...button,
    })),
    ...(sftpFilesGroup
      ? [{ kind: "dropdown" as const, ...sftpFilesGroup }]
      : []),
    runDeliveryPipelineButton,
    ...(pushToSftpGroup
      ? [{ kind: "dropdown" as const, ...pushToSftpGroup }]
      : []),
  ];
};
